import type { JwtPayload } from 'jwt-decode'
import type { SiteConfigDto, Company as CompanyDto, ExhibitorReceipt } from './api'
import { type PeriodStatus } from './enum'

export class SiteConfig {
  public readonly fairMasterId: number
  public readonly fairMasterCode: string
  public readonly siteUrl: string
  public readonly siteName: string
  public readonly siteNameEng: string
  public readonly subFairMasterIds: number[]
  public readonly status: string
  public readonly logoUrl: string
  public readonly logoUrlEng?: string
  public readonly basePath?: string
  public readonly useEnglishPage?: boolean
  public readonly supportLocales: string[]
  public readonly primaryColor?: string
  public readonly contactTel?: string
  public readonly contactEmail?: string
  public readonly exhibitorLabel?: string
  public readonly organizerName?: string
  public readonly organizerNameEng?: string
  public readonly organizerCeoName?: string
  public readonly organizerCeoNameEng?: string
  public readonly organizerBizNo?: string
  public readonly organizerAddress?: string
  public readonly organizerAddressEng?: string
  public readonly organizerZipCode?: string
  public readonly organizerFaxNo?: string
  public readonly organizerTelNo?: string
  public readonly organizerEmail?: string
  public readonly organizerHomepage?: string

  constructor(data: SiteConfigDto) {
    this.fairMasterId = data.fairMasterId
    this.fairMasterCode = data.fairMasterCode
    this.siteUrl = data.siteUrl
    this.siteName = data.siteName
    this.siteNameEng = data.siteNameEng
    this.subFairMasterIds = data.subFairMasterIds?.includes(',') ? data.subFairMasterIds.split(',').map((id) => Number(id)) : []
    this.status = data.status as string
    this.logoUrl = data.logoUrl as string
    this.logoUrlEng = data.logoUrlEng
    this.basePath = data.basePath ?? '/'
    this.supportLocales = data.supportLocales
    this.primaryColor = data.primaryColor
    this.useEnglishPage = data.useEnglishPage ?? false
    this.contactTel = data.contactTel
    this.contactEmail = data.contactEmail
    this.exhibitorLabel = data?.exhibitorLabel
    this.organizerName = data?.organizerName
    this.organizerNameEng = data?.organizerNameEng
    this.organizerCeoName = data?.organizerCeoName
    this.organizerCeoNameEng = data?.organizerCeoNameEng
    this.organizerBizNo = data?.organizerBizNo
    this.organizerAddress = data?.organizerAddress
    this.organizerAddressEng = data?.organizerAddressEng
    this.organizerZipCode = data?.organizerZipCode
    this.organizerFaxNo = data?.organizerFaxNo
    this.organizerTelNo = data?.organizerTelNo
    this.organizerEmail = data?.organizerEmail
    this.organizerHomepage = data?.organizerHomepage
  }
}

export class Member {
  public loginId?: string
  public companyId: number
  public companyName: string
  public companyNameEng?: string
  public telephone?: string
  public email?: string
  public bizNumber?: string
  public foreignFlag?: string

  public static readonly Empty = new Member()

  constructor(data: any | null = null) {
    this.loginId = data?.loginId
    this.companyId = data?.companyId
    this.companyName = data?.companyName
    this.companyNameEng = data?.companyNameEng
    this.telephone = data?.telephone
    this.email = data?.email
    this.bizNumber = data?.bizNumber
    this.foreignFlag = data?.foreignFlag
  }

  public isEmpty(): boolean {
    return this.loginId === undefined
  }
}

export class Company {
  id?: number
  name?: string
  nameEng?: string
  brandName?: string
  brandNameEng?: string
  bizNumber?: string
  ceoName?: string
  zipCode?: string
  addr?: string
  addrDetail?: string
  addrEng?: string
  homepage?: string
  nation?: string
  nationDesc?: string
  bizType?: string
  categoryType?: string
  isOverseaCompany?: boolean
  ceoEmail?: string
  ceoTelNo?: string
  companyTelNo?: string
  companyFaxNo?: string
  companyEmail?: string

  public static readonly Empty = new Company()

  constructor(data: CompanyDto | null = null) {
    this.id = data?.id
    this.name = data?.name
    this.nameEng = data?.nameEng
    this.brandName = data?.brandName
    this.brandNameEng = data?.brandNameEng
    this.bizNumber = data?.bizNumber
    this.ceoName = data?.ceoName
    this.zipCode = data?.zipCode
    this.addr = data?.addr
    this.addrDetail = data?.addrDetail
    this.addrEng = data?.addrEng
    this.nation = data?.nation
    this.nationDesc = data?.nationDesc
    this.homepage = data?.homepage
    this.bizType = data?.bizType
    this.categoryType = data?.categoryType
    this.isOverseaCompany = data?.isOverseaCompany
    this.ceoEmail = data?.ceoEmail
    this.ceoTelNo = data?.ceoTelNo
    this.companyTelNo = data?.companyTelNo
    this.companyFaxNo = data?.companyFaxNo
    this.companyEmail = data?.companyEmail
  }
}

export class Exhibitor {
  public companyId?: number
  public fairId?: number
  public exhibitorId?: number
  public encryptExhibitorId?: string
  public status?: 'NotSet' | 'CompleteApproval' | 'WaitApproval'
  public boothNumber?: string
  public receiptList?: ExhibitorReceipt[]

  static readonly Empty: Exhibitor = new Exhibitor()

  constructor(data: any | null = null) {
    this.companyId = data?.companyId
    this.fairId = data?.fairId
    this.exhibitorId = data?.exhibitorId
    this.encryptExhibitorId = data?.encryptExhibitorId
    this.status = data?.status
    this.boothNumber = data?.boothNumber
  }

  public isEmpty(): boolean {
    return this.exhibitorId === undefined
  }

  public isCompleteApproval(): boolean {
    return this.status === 'CompleteApproval'
  }

  public isWaitApproval(): boolean {
    return this.status === 'WaitApproval'
  }
}

export interface CompanyJoinDto {
  bizNumber: string
  companyName: string
  companyNameEng: string
  ceoName: string
  email: string
  telephone: string
  fax?: string
  homepageUrl?: string
  zipCode: string
  addr: string
  addrDetail: string
  bizType: string // 업태
  industry: string // 업종
  loginId: string
  password: string
  passwordConfirm: string
  isOverseaCompany: boolean
}

export interface Address {
  zipCode: string
  addr: string
  addrDetail: string
}
export interface PeriodData {
  IS_TODAY_IN_RANGE: 'Y' | 'N'
  START_DATE: string
  END_DATE: string
  PERIOD_STATUS: PeriodStatus
  [key: string]: any
}

export interface FairPeriod {
  code: string
  data: PeriodData
}

export interface PrizesItem {
  useYn: string
  prizesId?: number | null
  exhibitorId?: string | null
  fairId?: number | null
  name: string | null
  qty: string | number | null
  price: string | number | null
  description: string | null
  imageUrl: string | null
  shippingType: string | null
  shippingFee: string | number | null
  file: File | null
  createUser: string | null | undefined
}

export interface Terms {
  id: number
  name: string
  name_eng: string
  label: string
  label_eng: string
  must: boolean
  checked: boolean
  isVisible: boolean
  content: string
}

export interface ExtFile extends File {
  objectURL?: string
}

export interface InviteToken extends JwtPayload {
  FAIR_ID: number
  FAIR_MASTER_ID: number
  FAIR_MASTER_CD: string
  COMPANY_ID: number
  COMPANY_NAME: string
  LANG_TYPE: string
  IVCTID: number | null
}

export class CanvasItem {
  public Name?: string
  public isVisible?: boolean = true
  public X?: number = 0
  public Y?: number = 0
  public Draggable?: boolean = true

  constructor(data: Partial<CanvasItem>) {
    Object.assign(this, data)
  }
}

export class CanvasTextItem extends CanvasItem {
  public Text?: string | null = null
  public FontSize?: number = 20
  public FontFamily?: string = 'Noto sans kr'
  public FontStyle?: string = 'bold'
  public Wrap?: string = 'none'
  public Align?: string = 'center'
  public VerticalAlign?: string = 'middle'
  public Fill?: string = '#000000'

  constructor(data: Partial<CanvasTextItem>) {
    super(data)
    Object.assign(this, data)
  }
}

export class CanvasImageItem extends CanvasItem {
  public Width?: number
  public Height?: number
  public Image?: object = new Image()
  public ImageUrl?: string | null = ''

  constructor(data: Partial<CanvasImageItem>) {
    super(data)
    Object.assign(this, data)
  }
}

export interface InviteFormOptions {
  INVITATION_TITLE: string
  INVITATION_END_DATE: string
  USE_AGE_NOTICE: boolean
  AGE_NOTICE: {
    minAge: number
    notice: string
  }
  USE_VISITOR_NAME_ENG: boolean
  USE_COMPANY: boolean
  USE_COMPANY_ENG: boolean
  USE_DEPT: boolean
  USE_DEPT_ENG: boolean
  USE_POSITION: boolean
  USE_POSITION_ENG: boolean
  USE_SURVEY: boolean
  USE_SURVEY_INTEREST_UI: boolean
  USE_INTEREST: boolean
  INTEREST: string[]
  USE_OCCUPATION: boolean
  OCCUPATION: string[]
  USE_NATION: boolean
  USE_MARKETING_AGREE_POPUP: boolean
}
